import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Typography } from '@mui/material';

import { useActiveImports } from 'api/imports/importsV2/hooks/useActiveImports';
import { useCreateProposal } from 'api/projectVersioning/hooks/useCreateProposal';
import { useDiscardProposal } from 'api/projectVersioning/hooks/useDiscardProposal';
import { useGetCurrentProposal } from 'api/projectVersioning/hooks/useGetCurrentProposal';
import { useMergeProposal } from 'api/projectVersioning/hooks/useMergeProposal';
import { useProposalContext } from 'app/routes/projects/hooks/useProjectPath';
import { BaseDialog } from 'components/surfaces/BaseDialog/BaseDialog';
import { ConfirmationDialog } from 'components/surfaces/dialogs/ConfirmationDialog';
import { useCloseNodeDetailsPanelInGrid } from 'pages/projects/projectPage/selectedProject/nodeDetails/hooks/useCloseNodeDetailsPanel';
import { useIsImportInProgressForProject } from 'state/imports/importsInProgress';
import { PROPOSAL_CHANGES_PANEL_STORE } from 'state/proposalChanges/proposalChangesStore';

import { useAllProposalsData } from '../hooks/useAllProposalsData';
import { useEligibilityGate } from '../hooks/useEligibilityGate';
import { getPathForTargetContext } from '../utils/getPathForTargetContext';
import { ProposalMenu } from './ProposalMenu';
import { ProposalMenuButton } from './ProposalMenuButton';
import { AllProposalsContent } from './all-proposals-content/AllProposalsContent';
import { ActionBlockedDialog } from './dialogs/ActionBlockedDialog';
import { CreateProposalDialog } from './dialogs/CreateProposalDialog';
import { DiscardProposalDialog } from './dialogs/DiscardProposalDialog';

export type ProposalContainerProps = {
  title?: string;
  navigateTo: (path: string, options?: { replace?: boolean }) => void;
};

const ProposalContainer: React.FC<ProposalContainerProps> = ({ title, navigateTo }) => {
  const location = useLocation();
  const [proposalMenuAnchorEl, setProposalMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isProposalDialogOpen, setIsProposalDialogOpen] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [isSeeAllProposalsOpen, setIsSeeAllProposalsOpen] = useState(false);
  const { isInProposal, projectId = '', proposalId = '' } = useProposalContext();
  const closeNodeDetailsPanel = useCloseNodeDetailsPanelInGrid();
  const { isImportInProgress, isImportStatusLoading } = useIsImportInProgressForProject(projectId);
  useActiveImports(projectId);

  const routeToLiveView = useMemo(
    () =>
      getPathForTargetContext({
        pathname: location.pathname,
        projectId,
        currentProposalId: proposalId,
        targetProposalId: undefined
      }),
    [location.pathname, projectId, proposalId]
  );

  const { mutateAsync: createProposal, isPending: isCreateProposalPending } = useCreateProposal();
  const { mutateAsync: discardAsync, isPending: isDiscardPending } = useDiscardProposal();
  const { mutateAsync: mergeAsync, isPending: isMergePending } = useMergeProposal();
  const { blockedState, checkAndOpen, clearBlocked } = useEligibilityGate(projectId, proposalId);
  const { activeProposals, isActiveProposalsLoading } = useAllProposalsData();
  const { data: proposalData, isLoading: isGetCurrentProposalLoading } = useGetCurrentProposal();
  const { name: currentProposalName } = proposalData || {};

  const handleProposalTitleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setProposalMenuAnchorEl(event.currentTarget);
  }, []);

  const navigateToProposal = useCallback(
    (targetProposalId: string) => {
      setIsProposalDialogOpen(false);
      setProposalMenuAnchorEl(null);
      navigateTo(
        getPathForTargetContext({
          pathname: location.pathname,
          projectId,
          currentProposalId: proposalId,
          targetProposalId: targetProposalId
        })
      );
    },
    [location.pathname, navigateTo, projectId, proposalId]
  );

  const handleCreateProposal = useCallback(
    async (name: string) => {
      if (!projectId) {
        return;
      }
      // A failure is already reported by useCreateProposal's onError; swallow it so it isn't unhandled.
      const result = await createProposal({ projectId, payload: { name } }).catch(() => undefined);
      const proposalId = result?.id;

      if (!proposalId) {
        return;
      }

      navigateToProposal(proposalId);
    },
    [createProposal, navigateToProposal, projectId]
  );

  const onCloseMenu = useCallback(() => {
    setProposalMenuAnchorEl(null);
  }, []);

  const openProposalDialog = useCallback(() => {
    onCloseMenu();
    setIsProposalDialogOpen(true);
  }, [onCloseMenu]);

  const openSeeAllProposals = useCallback(() => {
    onCloseMenu();
    setIsSeeAllProposalsOpen(true);
  }, [onCloseMenu]);

  const openReviewChanges = useCallback(() => {
    onCloseMenu();
    closeNodeDetailsPanel();
    if (projectId) {
      PROPOSAL_CHANGES_PANEL_STORE.getState().open(projectId);
    }
  }, [onCloseMenu, closeNodeDetailsPanel, projectId]);

  const openDiscardDialog = useCallback(async () => {
    onCloseMenu();
    await checkAndOpen('discard', () => setIsDiscardDialogOpen(true));
  }, [onCloseMenu, checkAndOpen]);

  const openMergeDialog = useCallback(async () => {
    onCloseMenu();
    await checkAndOpen('merge', () => setIsMergeDialogOpen(true));
  }, [onCloseMenu, checkAndOpen]);

  const handleDiscardProposal = useCallback(async () => {
    if (!projectId || !proposalId) {
      return;
    }
    await discardAsync({ projectId, proposalId });
    setIsDiscardDialogOpen(false);
    navigateTo(routeToLiveView, { replace: true });
  }, [discardAsync, navigateTo, projectId, proposalId, routeToLiveView]);

  const handleMergeProposal = useCallback(async () => {
    if (!projectId || !proposalId) {
      return;
    }
    await mergeAsync({ projectId, proposalId });
    setIsMergeDialogOpen(false);
    navigateTo(routeToLiveView, { replace: true });
  }, [mergeAsync, navigateTo, projectId, proposalId, routeToLiveView]);

  return (
    <>
      <ProposalMenuButton
        isInProposal={isInProposal}
        title={title}
        proposalName={currentProposalName}
        loading={isActiveProposalsLoading || isGetCurrentProposalLoading}
        isMenuOpen={!!proposalMenuAnchorEl}
        onClick={handleProposalTitleClick}
      />
      {proposalMenuAnchorEl && (
        <ProposalMenu
          isInProposal={isInProposal}
          open={!!proposalMenuAnchorEl}
          anchorEl={proposalMenuAnchorEl}
          onClose={onCloseMenu}
          onCreateProposal={openProposalDialog}
          onMergeProposal={openMergeDialog}
          onDiscardProposal={openDiscardDialog}
          onReviewChanges={openReviewChanges}
          onSeeAllProposals={openSeeAllProposals}
          viewLiveUrl={routeToLiveView}
          proposals={activeProposals}
          disabled={isImportInProgress}
          loading={isImportStatusLoading}
        />
      )}

      {isProposalDialogOpen && projectId && (
        <CreateProposalDialog
          onConfirm={(name: string) => handleCreateProposal(name)}
          onClose={() => setIsProposalDialogOpen(false)}
          isLoading={isCreateProposalPending}
        />
      )}

      {isDiscardDialogOpen && isInProposal && (
        <DiscardProposalDialog
          open
          proposalName={currentProposalName ?? ''}
          onConfirm={handleDiscardProposal}
          onClose={() => setIsDiscardDialogOpen(false)}
          loading={isDiscardPending || isImportStatusLoading}
          disabled={isImportInProgress}
        />
      )}

      {isMergeDialogOpen && isInProposal && (
        <ConfirmationDialog
          title='Merge proposal?'
          id='merge-proposal-dialog'
          onClose={() => setIsMergeDialogOpen(false)}
          onConfirm={handleMergeProposal}
          confirmButtonText='Merge proposal'
          cancelButtonText='Cancel'
          loading={isMergePending || isImportStatusLoading}
          disabled={isImportInProgress}
          open
        >
          <Typography>
            Merging {currentProposalName} will apply its changes to the live project. Review changes first if
            you&apos;re unsure.
          </Typography>
        </ConfirmationDialog>
      )}

      {blockedState && isInProposal && (
        <ActionBlockedDialog
          open
          onClose={clearBlocked}
          eligibleUsers={blockedState.users}
          action={blockedState.action}
        />
      )}

      {isSeeAllProposalsOpen && (
        <BaseDialog
          title='All proposals'
          id='see-all-proposals-dialog'
          actions={{
            cancel: { onClick: () => setIsSeeAllProposalsOpen(false) }
          }}
          open
          fullWidth
          hideTitleClose
        >
          <AllProposalsContent
            navigateToProposal={navigateToProposal}
            closeDialog={() => setIsSeeAllProposalsOpen(false)}
            projectId={projectId}
          />
        </BaseDialog>
      )}
    </>
  );
};

export default ProposalContainer;
